import React, { useState } from "react"
import { useSelector } from "react-redux"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import FilterPagination from "pages/parts/FilterPagination"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const PAGE_SIZE = 50

const kindOptions = [
	{ id: "all", name: "Все выплаты" },
	{ id: "expenses", name: "Без поставщиков и зарплаты" },
]

function ExpenseKpi({ title, value, sublabel, icon, color = "#d23434" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div
						className="d-flex align-items-center justify-content-center"
						style={{ width: "30px", height: "30px", borderRadius: "10px", background: `${color}18`, color }}
					>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function TopExpenseCard({ item, index, total }) {
	const colors = ["#d23434", "#f59f00", "#4c6ef0", "#12b886"]
	const color = colors[index % colors.length]
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between gap-2">
					<div>
						<div style={mutedStyle}>Топ расход #{index + 1}</div>
						<h6 className="mb-1 mt-1" style={{ fontWeight: 800, minHeight: "36px" }}>{item.purpose_name}</h6>
					</div>
					<span
						className="d-inline-flex align-items-center justify-content-center"
						style={{ width: "28px", height: "28px", borderRadius: "50%", background: `${color}18`, color, fontWeight: 800 }}
					>
						{index + 1}
					</span>
				</div>
				<div className="d-flex align-items-baseline justify-content-between gap-2 mt-2">
					<div style={{ fontSize: "22px", fontWeight: 800 }}>{item.amount_short}</div>
					<div style={{ color, fontWeight: 700 }}>{Number(item.share_percent || 0).toFixed(1)}%</div>
				</div>
				<div className="progress mt-2" style={{ height: "6px", background: "var(--dark-surface-2, #f1f4f8)" }}>
					<div className="progress-bar" style={{ width: `${total > 0 ? item.share_percent : 0}%`, background: color }}></div>
				</div>
				<div style={{ ...mutedStyle, marginTop: "8px" }}>
					{formatMoney(item.quantity, 0)} операций · {formatMoney(item.branch_count, 0)} филиалов
				</div>
			</div>
		</div>
	)
}

function TopExpensesChart({ items }) {
	const series = [{ name: "Расход", data: items.map(item => Number(item.amount || 0)) }]
	const options = {
		chart: { type: "bar", toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: "62%" } },
		colors: ["#d23434"],
		dataLabels: { enabled: false },
		grid: { borderColor: "#eef2f6" },
		xaxis: {
			categories: items.map(item => item.purpose_name),
			labels: { formatter: value => formatMoney(value, 0) },
		},
		tooltip: { y: { formatter: value => `${formatMoney(value, 0)}` } },
	}

	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3">Топ расходов</h5>
				<ReactApexChart options={options} series={series} type="bar" height={Math.max(260, items.length * 42)} />
			</div>
		</div>
	)
}

function ExpenseTable({ data, onPaginate }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	const expenses = data?.expenses || []
	const page = data?.page || 0
	const size = data?.size || PAGE_SIZE

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
					<h5 className="mb-0">Список расходов по филиалам</h5>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "14px", padding: "4px 10px", color: "var(--dark-text-muted, #5b6472)" }}>
						{formatMoney(data?.total || 0, 0)} записей
					</span>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Дата</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Филиал</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Статья</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Тип оплаты</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Кассир</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Комментарий</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Сумма</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Доля</th>
							</tr>
						</thead>
						<tbody>
							{expenses.length === 0 && (
								<tr><td colSpan="9" className="text-muted text-center py-4">Нет данных</td></tr>
							)}
							{expenses.map((expense, index) => {
								const borderBottom = index === expenses.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={expense.id}>
										<td className="py-3 text-muted" style={{ borderBottom }}>{page * size + index + 1}</td>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{expense.date_label}</td>
										<td className="py-3" style={{ borderBottom }}>{expense.pos_name}</td>
										<td className="py-3" style={{ borderBottom }}>
											<div style={{ fontWeight: 600 }}>{expense.purpose_name}</div>
											{expense.kind !== "expense" && <div style={mutedStyle}>{expense.kind_label}</div>}
										</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{expense.payment_type_name}</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{expense.cashier_login || "—"}</td>
										<td className="py-3 text-muted" style={{ borderBottom, maxWidth: "260px" }}>{expense.note || "—"}</td>
										<td className="py-3 text-end" style={{ borderBottom, fontWeight: 800, color: "var(--dark-fg-danger, #d23434)" }}>{expense.amount_short}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{Number(expense.share_percent || 0).toFixed(1)}%</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
				{data && (
					<FilterPagination
						pagination={{ url: `?page=${page}`, response: { total: data.total }, size }}
						paginate={onPaginate}
					/>
				)}
			</div>
		</div>
	)
}

function ExpenseAnalysis() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: reduxAccount.pos_id ?? -1,
		kind: "all",
		purpose_ids: [],
		payment_type_ids: [],
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["expense_purposes", "payment_types"], draft.pos_id)

	async function searchWithFilter(filter, page = 0) {
		const response = await GET(buildAnalyticsUrl("/analytics/expense-analysis", filter, { page, size: PAGE_SIZE }), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// Excel — все строки по применённому фильтру, а не только текущая страница
	async function exportExcel() {
		const response = await GET(buildAnalyticsUrl("/analytics/expense-analysis", applied, { page: 0, size: 10000 }), {}, { loader: true })
		if (!httpOk(response)) return
		const exportData = response.data
		const rows = (exportData.expenses || [])
			.map(item => `<tr><td>${item.date_label}</td><td>${item.pos_name}</td><td>${item.purpose_name}</td><td>${item.kind_label}</td><td>${item.payment_type_name}</td><td>${item.cashier_login || ""}</td><td>${item.note || ""}</td><td>${item.amount}</td></tr>`)
			.join("")
		const html = `<table border="1"><thead><tr><th>Дата</th><th>Филиал</th><th>Статья</th><th>Вид</th><th>Тип оплаты</th><th>Кассир</th><th>Комментарий</th><th>Сумма</th></tr></thead><tbody>${rows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `expense-analysis-${exportData.start_date}_${exportData.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const summary = data?.summary || {}
	const topExpenses = data?.top_expenses || []
	const excludedTotal = Number(summary.excluded_supplier || 0) + Number(summary.excluded_staff || 0)

	return (
		<>
			<AnalyticsFilterBar
				title="Анализ расходов"
				icon="uil-money-withdraw"
				subtitle="Топ расходов и детализация по филиалам"
				filters={filters}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<StandardFields filters={filters} options={options} orderType={false} source={false} />
				<FilterField label="Статья">
					<FilterSelect isMulti options={options.expensePurposes} allLabel="Все статьи" value={draft.purpose_ids} onChange={(ids) => patch({ purpose_ids: ids })} />
				</FilterField>
				<FilterField label="Способ оплаты">
					<FilterSelect isMulti options={options.paymentTypes} allLabel="Все способы" value={draft.payment_type_ids} onChange={(ids) => patch({ payment_type_ids: ids })} />
				</FilterField>
				<FilterField label="Показывать">
					<FilterSelect options={kindOptions} value={draft.kind} onChange={(id) => patch({ kind: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			{data?.kind === "expenses" && excludedTotal > 0 && (
				<div className="alert alert-light border d-flex align-items-center gap-2 py-2" style={{ fontSize: "13px" }}>
					<i className="uil uil-info-circle"></i>
					<span>
						Не входят в расходы: оплаты поставщикам <b>{summary.excluded_supplier_short}</b> (товар уже в себестоимости)
						и выплаты сотрудникам <b>{summary.excluded_staff_short}</b>. Чтобы увидеть их, выберите «Все выплаты».
					</span>
				</div>
			)}

			{data?.kind === "all" && summary.by_kind && (
				<div className="alert alert-light border d-flex flex-wrap align-items-center gap-3 py-2" style={{ fontSize: "13px" }}>
					<i className="uil uil-chart-pie"></i>
					<span>Расходы <b>{summary.by_kind.expense?.amount_short || "0"}</b></span>
					<span>Оплаты поставщикам <b>{summary.by_kind.supplier?.amount_short || "0"}</b></span>
					<span>Выплаты сотрудникам <b>{summary.by_kind.staff?.amount_short || "0"}</b></span>
				</div>
			)}

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ExpenseKpi title={data?.kind === "all" ? "Всего списано" : "Всего расходов"} value={summary.total_amount_short || "0"} sublabel="за выбранный период" icon="uil-money-withdraw" />
				</div>
				<div className="col-xl-3 col-md-6">
					<ExpenseKpi title="Операций" value={formatMoney(summary.transactions_count, 0)} sublabel="расходных записей" icon="uil-receipt" color="#4c6ef0" />
				</div>
				<div className="col-xl-3 col-md-6">
					<ExpenseKpi title="Средний расход" value={summary.average_amount_short || "0"} sublabel="на одну операцию" icon="uil-calculator" color="#f59f00" />
				</div>
				<div className="col-xl-3 col-md-6">
					<ExpenseKpi title="Топ статья" value={summary.top_purpose_name || "—"} sublabel={`${formatMoney(summary.branch_count, 0)} филиалов с расходами`} icon="uil-chart-pie-alt" color="#12b886" />
				</div>
			</div>

			{topExpenses.length > 0 && (
				<div className="row g-3 mb-3">
					{topExpenses.slice(0, 4).map((item, index) => (
						<div className="col-xl-3 col-md-6" key={item.purpose_id}>
							<TopExpenseCard item={item} index={index} total={summary.total_amount || 0} />
						</div>
					))}
				</div>
			)}

			<TopExpensesChart items={topExpenses} />
			<ExpenseTable data={data} onPaginate={(page) => searchWithFilter(applied, page)} />
		</>
	)
}

export default ExpenseAnalysis
